import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import '../EmergencyApproval/styles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

export default function EmergencyDualApproval() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const requestId = searchParams.get('requestId');
  const approvalType = searchParams.get('type'); // 'contact' or 'advisor'

  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [requestsList, setRequestsList] = useState([]); // pending advisor requests when no requestId
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [decision, setDecision] = useState(null); // 'approved' or 'denied'
  const [timeRemaining, setTimeRemaining] = useState('');

  const isContact = approvalType === 'contact';
  const isAdvisor = approvalType === 'advisor';
  const showAdvisorList = isAdvisor && isAuthenticated && !requestId;

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      const returnTo = `/emergency-approvals${requestId ? `?requestId=${requestId}` : ''}${approvalType ? `${requestId ? '&' : '?'}type=${approvalType}` : ''}`;
      loginWithRedirect({ appState: { returnTo } });
      return;
    }

    // Advisor list view: no requestId, type=advisor → fetch list of pending requests
    if (showAdvisorList) {
      fetchAdvisorList();
      return;
    }

    if (!requestId || !approvalType) {
      setError('Invalid approval link. Missing request ID or approval type. Advisors: use the link from your email or go to Emergency approvals with ?type=advisor');
      setLoading(false);
      return;
    }

    fetchRequest();
  }, [requestId, approvalType, isAuthenticated, authLoading, showAdvisorList]);

  // Countdown timer for advisor auto-grant
  useEffect(() => {
    if (!request?.advisorAutoGrantAt || !isAdvisor) return;

    const interval = setInterval(() => {
      const now = new Date();
      const autoGrant = new Date(request.advisorAutoGrantAt);
      const diff = autoGrant - now;

      if (diff <= 0) {
        setTimeRemaining('Auto-grant triggered');
        clearInterval(interval);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [request?.advisorAutoGrantAt, isAdvisor]);

  const fetchAdvisorList = async () => {
    try {
      setListLoading(true);
      setError('');
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
      });
      const res = await fetch(`${API_BASE_URL}/api/emergency-access/pending-advisor-approvals`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load pending requests');
      }
      const data = await res.json();
      setRequestsList(data.requests || []);
    } catch (err) {
      console.error('Error fetching advisor list:', err);
      setError(err.message || 'Failed to load pending requests');
    } finally {
      setListLoading(false);
    }
  };

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
      });

      // Use the request-status endpoint to get basic info
      const response = await fetch(
        `${API_BASE_URL}/api/emergency-access/request-status/${requestId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // If 403, the current user isn't the requester — that's expected for contacts/advisors
      // Try the appropriate pending approvals endpoint instead
      if (response.status === 403 || response.status === 404) {
        // Fetch from pending approvals list to get request details
        const listEndpoint = isContact
          ? '/api/emergency-access/pending-contact-approvals'
          : '/api/emergency-access/pending-advisor-approvals';

        const listResponse = await fetch(`${API_BASE_URL}${listEndpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!listResponse.ok) {
          throw new Error('Failed to load approval requests. You may not have permission to view this request.');
        }

        const listData = await listResponse.json();
        const matchingRequest = (listData.requests || []).find(r => r.id === requestId);

        if (!matchingRequest) {
          throw new Error('This request was not found in your pending approvals. It may have already been processed or you may not be authorized.');
        }

        setRequest(matchingRequest);
      } else if (response.ok) {
        const data = await response.json();
        setRequest(data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch request');
      }
    } catch (err) {
      console.error('Error fetching request:', err);
      setError(err.message || 'Failed to load approval request');
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (approved) => {
    const action = approved ? 'approve' : 'deny';
    const confirmMsg = isContact
      ? approved
        ? 'Are you sure you want to approve this emergency access request? The family advisor will be notified for final review.'
        : 'Are you sure you want to deny this emergency access request? The requester will be notified immediately.'
      : approved
        ? 'Are you sure you want to approve this emergency access? The requester will be granted vault access immediately.'
        : 'Are you sure you want to deny this emergency access? The requester will be notified immediately.';

    if (!window.confirm(confirmMsg)) return;

    try {
      setSubmitting(true);
      setError('');

      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
      });

      const endpoint = isContact
        ? `/api/emergency-access/contact-approval/${requestId}`
        : `/api/emergency-access/advisor-approval/${requestId}`;

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approved })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} request`);
      }

      setSuccess(true);
      setDecision(approved ? 'approved' : 'denied');
    } catch (err) {
      console.error(`Error ${action}ing request:`, err);
      setError(err.message || `Failed to ${action} request`);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || (showAdvisorList ? listLoading : loading)) {
    return (
      <div className="emergency-approval-container">
        <LoadingSpinner type="page" message={showAdvisorList ? 'Loading pending requests...' : 'Loading approval request...'} />
      </div>
    );
  }

  // Advisor list view: show all pending emergency access requests for this advisor
  if (showAdvisorList) {
    return (
      <div className="emergency-approval-container">
        <div className="approval-card">
          <div className="approval-header">
            <h1>Emergency access – advisor review</h1>
            <p className="approval-subtitle">
              Emergency access requests that need your approval. Click Review to approve or deny.
            </p>
          </div>
          {error && (
            <div className="approval-error-message">
              <p>{error}</p>
              <button type="button" className="btn-primary" onClick={() => fetchAdvisorList()}>Try again</button>
            </div>
          )}
          {!error && requestsList.length === 0 && (
            <div className="approval-details" style={{ padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: '#e2c89c', marginBottom: '1rem' }}>No pending emergency access requests.</p>
              <button type="button" className="btn-primary" onClick={() => navigate('/')}>Go to Dashboard</button>
            </div>
          )}
          {!error && requestsList.length > 0 && (
            <div className="approval-details">
              <ul className="emergency-approval-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {requestsList.map((r) => (
                  <li key={r.id} style={{ border: '1px solid rgba(191,169,122,0.3)', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                      <strong style={{ color: '#e2c89c' }}>{r.requesterName || 'Unknown'}</strong>
                      <span style={{ opacity: 0.9 }}> → </span>
                      <strong>{r.targetName || 'Unknown'}</strong>
                      <span style={{ fontSize: '0.9rem', opacity: 0.8, display: 'block', marginTop: 4 }}>
                        Requested {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => navigate(`/emergency-approvals?requestId=${r.id}&type=advisor`)}
                    >
                      Review
                    </button>
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: '1.5rem' }}>
                <button type="button" className="btn-primary" onClick={() => fetchAdvisorList()}>Refresh list</button>
                <button type="button" className="btn-primary" style={{ marginLeft: 8, background: 'transparent', border: '1px solid rgba(191,169,122,0.5)' }} onClick={() => navigate('/')}>Go to Dashboard</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="emergency-approval-container">
        <div className="approval-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="emergency-approval-container">
        <div className="approval-error">
          <h2>Request Not Found</h2>
          <p>The approval request could not be found or has already been processed.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isExpired = request.expiresAt && new Date(request.expiresAt) < new Date();
  const alreadyDecided = isContact
    ? request.contactApproved !== null && request.contactApproved !== undefined
    : request.advisorApproved !== null && request.advisorApproved !== undefined;

  // Check if the request is in the correct status for this approval type
  const expectedStatus = isContact ? 'pending_contact_approval' : 'pending_advisor_approval';
  const statusMismatch = request.status && request.status !== expectedStatus;

  return (
    <div className="emergency-approval-container">
      <div className="approval-card">
        <div className="approval-header">
          <h1>
            {isContact ? 'Emergency Contact Approval' : 'Advisor Review'}
          </h1>
          <p className="approval-subtitle">
            {isContact
              ? 'An emergency access request requires your approval as the assigned emergency contact'
              : 'An emergency access request has been approved by the assigned contact and requires your review'}
          </p>
        </div>

        {success && (
          <div className="approval-success">
            <div className="success-icon">{decision === 'approved' ? '✓' : '✗'}</div>
            <p>
              Request {decision === 'approved' ? 'approved' : 'denied'} successfully!
            </p>
            {isContact && decision === 'approved' && (
              <p className="redirect-message">
                The family advisor has been notified and has 24 hours to review.
              </p>
            )}
            {isAdvisor && decision === 'approved' && (
              <p className="redirect-message">
                The requester has been notified and granted access.
              </p>
            )}
          </div>
        )}

        {error && request && (
          <div className="approval-error-message">
            <p>{error}</p>
          </div>
        )}

        {!success && (
          <>
            <div className="approval-details">
              <div className="detail-section">
                <h3>Request Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Requester:</span>
                    <span className="detail-value">{request.requesterName || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Vault Owner:</span>
                    <span className="detail-value">{request.targetName || request.ownerName || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Requested:</span>
                    <span className="detail-value">
                      {new Date(request.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Access Tier:</span>
                    <span className="detail-value">Tier 1 — Immediate Access</span>
                  </div>
                </div>
              </div>

              {isContact && (
                <div className="detail-section">
                  <h3>What Happens Next</h3>
                  <div className="access-info">
                    <div className="info-item">
                      <span className="info-icon">1.</span>
                      <div>
                        <strong>Your Approval</strong>
                        You are the assigned emergency contact. Your approval is the first step.
                      </div>
                    </div>
                    <div className="info-item">
                      <span className="info-icon">2.</span>
                      <div>
                        <strong>Advisor Review (24 hours)</strong>
                        After your approval, the family advisor has 24 hours to review and can deny if needed.
                      </div>
                    </div>
                    <div className="info-item">
                      <span className="info-icon">3.</span>
                      <div>
                        <strong>Access Granted</strong>
                        If the advisor approves (or doesn't respond within 24 hours), access is granted.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isAdvisor && (
                <div className="detail-section">
                  <h3>Advisor Review</h3>
                  <div className="access-info">
                    <div className="info-item">
                      <span className="info-icon">✓</span>
                      <div>
                        <strong>Contact Approved</strong>
                        The assigned emergency contact has already approved this request
                        {request.contactApprovedAt && (
                          <> on {new Date(request.contactApprovedAt).toLocaleString()}</>
                        )}.
                      </div>
                    </div>
                    {request.advisorAutoGrantAt && (
                      <div className="info-item">
                        <span className="info-icon">⏳</span>
                        <div>
                          <strong>Auto-Grant Countdown</strong>
                          If you take no action, access will be automatically granted.
                          {timeRemaining && (
                            <span style={{ display: 'block', color: '#ffa726', fontSize: '1.2rem', fontWeight: 'bold', marginTop: '0.5rem', fontFamily: "'Courier New', monospace" }}>
                              {timeRemaining}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {statusMismatch && (
                <div className="detail-section">
                  <div className="decision-made approved" style={
                    request.status === 'denied'
                      ? { background: 'rgba(211, 47, 47, 0.1)', border: '1px solid rgba(211, 47, 47, 0.3)' }
                      : request.status === 'granted'
                        ? { background: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)' }
                        : {}
                  }>
                    <span className="decision-icon">
                      {request.status === 'granted' ? '✓' : request.status === 'denied' ? '✗' : 'i'}
                    </span>
                    <div>
                      <strong>Status:</strong>
                      <span className="decision-value">
                        {request.status === 'granted' ? 'Access Granted'
                          : request.status === 'denied' ? 'Access Denied'
                          : request.status === 'pending_advisor_approval' ? 'Pending Advisor Review'
                          : request.status}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!alreadyDecided && !isExpired && !statusMismatch && (
              <div className="approval-actions">
                <button
                  onClick={() => handleDecision(true)}
                  disabled={submitting}
                  className="btn-approve"
                >
                  {submitting ? 'Processing...' : '✓ Approve Request'}
                </button>
                <button
                  onClick={() => handleDecision(false)}
                  disabled={submitting}
                  className="btn-deny"
                >
                  {submitting ? 'Processing...' : '✗ Deny Request'}
                </button>
              </div>
            )}

            {isExpired && !alreadyDecided && (
              <div className="approval-expired">
                <p>This request has expired and can no longer be approved or denied.</p>
                <button onClick={() => navigate('/')} className="btn-primary">
                  Go to Dashboard
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
